/**
 * What each manager actually does on draft day.
 *
 * Everybody in this league has a reputation and almost none of them are examined. "Luke always
 * goes running back twice", "Samant will not take a defense before round twelve", "Amit drafts
 * Cowboys" — these get said every August and nobody checks. There are 23 drafts and 3,756 picks
 * in the archive, so they can be checked.
 *
 * Two rules keep this honest, because a tendency engine is very easy to make lie:
 *
 *   NOTHING IS REPORTED WITHOUT A BASELINE. "Luke took a running back with his first pick in 6 of
 *   9 drafts" means nothing until you know the league takes one 61% of the time. What is
 *   interesting is the GAP, so every tendency carries the league rate beside the man's own and is
 *   ranked by the difference.
 *
 *   NOTHING IS REPORTED ON THIN EVIDENCE. Three drafts is an anecdote. Every tendency states how
 *   many drafts it saw and how many picks it rests on, and the ones below the floor are dropped
 *   rather than shown with a shrug — a projection built on two observations is worse than no
 *   projection, because it looks like knowledge.
 *
 * Keepers are excluded throughout. A keeper is a decision made in August about a player already
 * owned; it says nothing about how somebody drafts.
 */

import draftData from '../data/draft-history.json';
import { gmFor, knownGms, pairingVersion } from './identity.js';
import { BUNDLED_BANK } from './playerBank.js';

interface Pick {
  overall: number; round: number; pick: number; gm: string; player: string; keeper?: boolean;
}
interface DraftSeason { season: number; teams: number; rounds: number; picks: Pick[] }

const SEASONS = (draftData as unknown as { seasons: DraftSeason[] }).seasons;

/** Below this a "tendency" is an anecdote. Four drafts is the least that can show a habit. */
export const MIN_DRAFTS = 4;

/**
 * And below THIS there is nothing to say at all.
 *
 * Positional findings need the player bank to recognise the men he drafted, and it only knows
 * current players. A manager whose drafts are all pre-2019 resolves to nothing — he gets left out
 * rather than shown as a card with a draft count and no content, which reads like a bug.
 */
export const MIN_RESOLVED = 12;

/**
 * The manager behind a pick.
 *
 * The archive writes a MANAGER ID from 2009 and a TEAM NAME before that, exactly like the
 * standings did — and reading the team name as a person is the bug that once put "The Insane
 * Israeli" in the career table with an 0-0 record. So every pick goes through the same identity
 * overlay the records engine uses, and a pick whose owner cannot be resolved is dropped rather
 * than counted under a team name.
 */
let GM_IDS: Set<string> | null = null;
function ownerOf(season: number, raw: string): string | null {
  if (!GM_IDS) GM_IDS = new Set(knownGms());
  const up = raw.trim().toUpperCase();
  // From 2009 the archive already names the man. Before that it names his team, and only the
  // pairing overlay can turn that into a person.
  if (GM_IDS.has(up)) return up;
  return gmFor(season, { gm: null, team: raw, gmResolved: false } as never);
}

/* -- what we know about a drafted player ------------------------------------------------------- */

/**
 * Position and NFL team for a name, from the current player bank.
 *
 * This is the honest weak point and it is worth stating plainly: the bank knows who Ja'Marr Chase
 * is TODAY, not who he was in 2021, and it has never heard of most players drafted before about
 * 2019. So positional tendencies are computed only over the picks that resolve, and the count of
 * those picks is reported. A manager whose picks mostly predate the bank gets fewer tendencies,
 * not invented ones.
 */
const bankByName = new Map(
  BUNDLED_BANK.players.map((p) => [norm(p.name), p]),
);

function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
}

const posOf = (player: string): string | null => bankByName.get(norm(player))?.pos ?? null;
const teamOf = (player: string): string | null => {
  const p = bankByName.get(norm(player));
  return p?.team || p?.last || null;
};

/* -- the shape of a finding -------------------------------------------------------------------- */

export interface Tendency {
  /** Short enough to sit on a card. */
  label: string;
  /** His rate, 0-1. */
  rate: number;
  /** What everybody else does, so the reader can tell a habit from the game. */
  leagueRate: number;
  /** rate − leagueRate. Sorting key; the sign is the whole point. */
  edge: number;
  /** How many of his drafts this rests on. */
  drafts: number;
  /** The sentence shown under the label. */
  detail: string;
}

export interface ManagerTendencies {
  gm: string;
  drafts: number;
  seasons: number[];
  /** Non-keeper picks that resolved to a known player. */
  resolved: number;
  /** Every non-keeper pick he made, resolved or not: the denominator the head prints (Pass 16). */
  picks: number;
  tendencies: Tendency[];
  /** Positions he takes early, as a share of his first four rounds. Always shown. */
  earlyMix: Array<{ pos: string; share: number; leagueShare: number }>;
}

/* -- the engine -------------------------------------------------------------------------------- */

const realPicks = (s: DraftSeason) => s.picks.filter((p) => !p.keeper && p.gm && p.player);

/**
 * Memoised against the pairing version.
 *
 * `positionBias` is called once per candidate per pick, and the projection scans a thousand
 * candidates across forty picks — so an unmemoised `tendencies()` re-walked 3,756 archive picks
 * forty thousand times and took 43 seconds. On draft night that is a frozen screen. Keyed on the
 * pairing version so that naming a 2004 team still recomputes it.
 */
let cache: { key: string; rows: ManagerTendencies[] } | null = null;

export function tendencies(minDrafts = MIN_DRAFTS): ManagerTendencies[] {
  const key = `${pairingVersion()}|${minDrafts}`;
  if (cache?.key === key) return cache.rows;
  const rows = computeTendencies(minDrafts);
  cache = { key, rows };
  return rows;
}

function computeTendencies(minDrafts: number): ManagerTendencies[] {
  const byGm = new Map<string, { picks: Pick[]; seasons: Set<number> }>();
  const all: Pick[] = [];

  for (const s of SEASONS) {
    for (const raw of realPicks(s)) {
      const gm = ownerOf(s.season, raw.gm);
      if (!gm) continue;
      const p = { ...raw, gm };
      seasonIndex.set(p, s.season);
      const e = byGm.get(gm) ?? { picks: [], seasons: new Set<number>() };
      e.picks.push(p);
      e.seasons.add(s.season);
      byGm.set(gm, e);
      all.push(p);
    }
  }

  const out: ManagerTendencies[] = [];
  for (const [gm, e] of byGm) {
    if (e.seasons.size < minDrafts) continue;
    const resolved = e.picks.filter((p) => posOf(p.player)).length;
    if (resolved < MIN_RESOLVED) continue;
    const others = all.filter((p) => p.gm !== gm);
    out.push({
      gm,
      drafts: e.seasons.size,
      seasons: [...e.seasons].sort((a, b) => a - b),
      resolved,
      picks: e.picks.length,
      earlyMix: earlyMix(e.picks, others),
      tendencies: findings(e.picks, others, e.seasons),
    });
  }
  return out.sort((a, b) => b.drafts - a.drafts || a.gm.localeCompare(b.gm));
}

/** Share of his first four rounds spent on each position, against the league's. */
function earlyMix(mine: Pick[], others: Pick[]): ManagerTendencies['earlyMix'] {
  const share = (picks: Pick[]) => {
    const early = picks.filter((p) => p.round <= 4).map((p) => posOf(p.player)).filter(Boolean);
    const by = new Map<string, number>();
    for (const p of early) by.set(p!, (by.get(p!) ?? 0) + 1);
    return { by, total: early.length };
  };
  const me = share(mine);
  const them = share(others);
  return ['RB', 'WR', 'TE', 'QB', 'DEF', 'K']
    .map((pos) => ({
      pos,
      share: me.total ? (me.by.get(pos) ?? 0) / me.total : 0,
      leagueShare: them.total ? (them.by.get(pos) ?? 0) / them.total : 0,
    }))
    .filter((x) => x.share > 0 || x.leagueShare > 0.02);
}

function findings(mine: Pick[], others: Pick[], seasons: Set<number>): Tendency[] {
  const found: Tendency[] = [];
  const drafts = seasons.size;

  const add = (
    label: string, mineHits: number, mineOf: number,
    theirHits: number, theirOf: number, detail: (r: number, l: number) => string,
  ) => {
    if (mineOf < drafts || theirOf === 0) return;
    const rate = mineHits / mineOf;
    const leagueRate = theirHits / theirOf;
    // A gap under ten points is noise at this sample size, and a page of noise buries the
    // two findings that are real.
    if (Math.abs(rate - leagueRate) < 0.1) return;
    found.push({ label, rate, leagueRate, edge: rate - leagueRate, drafts, detail: detail(rate, leagueRate) });
  };

  /* -- opens with a running back ------------------------------------------------------------- */
  const firstOf = (picks: Pick[]) => {
    const by = new Map<string, Pick>();
    for (const p of [...picks].sort((a, b) => a.overall - b.overall)) {
      const key = `${p.gm}|${seasonOf(p)}`;
      if (!by.has(key)) by.set(key, p);
    }
    return [...by.values()];
  };
  const myFirsts = firstOf(mine);
  const theirFirsts = firstOf(others);
  for (const pos of ['RB', 'WR']) {
    add(
      `Opens ${pos}`,
      myFirsts.filter((p) => posOf(p.player) === pos).length,
      myFirsts.filter((p) => posOf(p.player)).length,
      theirFirsts.filter((p) => posOf(p.player) === pos).length,
      theirFirsts.filter((p) => posOf(p.player)).length,
      (r, l) => `Takes a ${pos} with his first pick ${pct(r)} of the time; the league does it ${pct(l)}.`,
    );
  }

  /* -- doubles up early ---------------------------------------------------------------------- */
  const doubled = (picks: Pick[], pos: string) => {
    const byDraft = new Map<string, string[]>();
    for (const p of [...picks].sort((a, b) => a.overall - b.overall)) {
      if (p.round > 3) continue;
      const key = `${p.gm}|${seasonOf(p)}`;
      byDraft.set(key, [...(byDraft.get(key) ?? []), posOf(p.player) ?? '?']);
    }
    const rows = [...byDraft.values()];
    return {
      hits: rows.filter((r) => r.filter((x) => x === pos).length >= 2).length,
      of: rows.length,
    };
  };
  for (const pos of ['RB', 'WR']) {
    const me = doubled(mine, pos);
    const them = doubled(others, pos);
    add(`${pos}-${pos} start`, me.hits, me.of, them.hits, them.of,
      (r, l) => `Takes two ${pos}s inside three rounds ${pct(r)} of the time, against ${pct(l)}.`);
  }

  /* -- defense early ------------------------------------------------------------------------- */
  const earlyDef = (picks: Pick[]) => {
    const byDraft = new Map<string, boolean>();
    for (const p of picks) {
      const key = `${p.gm}|${seasonOf(p)}`;
      const early = posOf(p.player) === 'DEF' && p.round <= 10;
      byDraft.set(key, (byDraft.get(key) ?? false) || early);
    }
    return { hits: [...byDraft.values()].filter(Boolean).length, of: byDraft.size };
  };
  const meDef = earlyDef(mine);
  const themDef = earlyDef(others);
  add('Defense early', meDef.hits, meDef.of, themDef.hits, themDef.of,
    (r, l) => `Has a defense by round 10 in ${pct(r)} of his drafts; the league ${pct(l)}.`);

  /* -- a club he keeps going back to --------------------------------------------------------- */
  /**
   * Restricted to recent drafts, and this restriction is load-bearing.
   *
   * The player bank knows where somebody plays TODAY, not where he played when he was drafted. Run
   * over twenty years that turns "Amit likes Buffalo" into "Amit drafted men who have since ended
   * up in Buffalo", which is not a tendency, it is roster churn. Over the last three drafts a
   * player's current club is usually the club he was drafted from, so the claim survives.
   */
  const CLUB_SINCE = Math.max(...SEASONS.map((s) => s.season)) - 2;
  const recent = (p: Pick) => seasonOf(p) >= CLUB_SINCE;

  const byTeam = new Map<string, number>();
  let mineWithTeam = 0;
  for (const p of mine.filter(recent)) {
    const t = teamOf(p.player);
    if (!t) continue;
    mineWithTeam += 1;
    byTeam.set(t, (byTeam.get(t) ?? 0) + 1);
  }
  const theirTeam = new Map<string, number>();
  let theirsWithTeam = 0;
  for (const p of others.filter(recent)) {
    const t = teamOf(p.player);
    if (!t) continue;
    theirsWithTeam += 1;
    theirTeam.set(t, (theirTeam.get(t) ?? 0) + 1);
  }
  const favourite = [...byTeam.entries()]
    .filter(([, n]) => n >= 3)
    .map(([team, n]) => ({
      team,
      rate: n / Math.max(1, mineWithTeam),
      leagueRate: (theirTeam.get(team) ?? 0) / Math.max(1, theirsWithTeam),
      n,
    }))
    .sort((a, b) => (b.rate - b.leagueRate) - (a.rate - a.leagueRate))[0];
  if (favourite && favourite.rate - favourite.leagueRate > 0.03) {
    found.push({
      label: `Likes ${favourite.team}`,
      rate: favourite.rate,
      leagueRate: favourite.leagueRate,
      edge: favourite.rate - favourite.leagueRate,
      drafts,
      detail: `Since ${CLUB_SINCE}, ${favourite.n} of his picks have been ${favourite.team} `
        + `players — ${pct(favourite.rate)} of what he drafts, against `
        + `${pct(favourite.leagueRate)} for everybody else.`,
    });
  }

  return found.sort((a, b) => Math.abs(b.edge) - Math.abs(a.edge));
}

/** Which season a pick belongs to. Filled as picks are rewritten with their resolved owner. */
const seasonIndex = new Map<Pick, number>();
const seasonOf = (p: Pick): number => seasonIndex.get(p) ?? 0;

const pct = (n: number) => `${Math.round(n * 100)}%`;

/** One manager, for a projection that needs to ask "what would he do here?". */
export function tendencyFor(gm: string): ManagerTendencies | null {
  return tendencies().find((t) => t.gm === gm) ?? null;
}

/**
 * How much this manager favours a position early, as a multiplier on a neutral expectation.
 *
 * Used by the projection. Deliberately gentle — a 2.4× swing on a position somebody takes twice
 * as often is real, but letting it run to 10× would produce a projection that says what the
 * engine already believed rather than what the board suggests.
 */
export function positionBias(gm: string, pos: string, round: number): number {
  const t = tendencyFor(gm);
  if (!t || round > 6) return 1;
  const mix = t.earlyMix.find((m) => m.pos === pos);
  if (!mix || mix.leagueShare <= 0) return 1;
  const raw = mix.share / mix.leagueShare;
  return Math.max(0.4, Math.min(2.5, raw));
}
